

/**
 * Reduce a user-supplied telephone string down to bare national digits.
 *
 * The field takes whatever the user types or pastes and -- crucially -- whatever
 * the browser autofills, so the value arrives in any of these shapes:
 *
 *   "07700900123"           national, with trunk prefix
 *   "+44 7700 900123"       international
 *   "+44 (0)7700 900123"    international with the trunk prefix left in
 *   "00447700900123"        international using the "00" prefix
 *
 * All of them have to reduce to "7700900123" before being recombined with the
 * selected dial code, otherwise we store "+44.4407700900123", which passes the
 * loose EPP regex used here but fails the country-specific regex in
 * countryToTelephoneCode.json later, at the payment stage.
 *
 * This must run against the raw string: a leading "+" is the only reliable
 * signal that the first digits are a country code rather than part of the
 * number, and it is gone the moment punctuation is stripped.
 *
 * @param {string} raw   whatever arrived in the input
 * @param {string} code  selected dial code, with or without its "+", e.g. "+44"
 * @returns {string} national digits
 */
function normaliseNationalNumber(raw, code) {
    const value = String(raw == null ? "" : raw).trim()
    const cc = String(code == null ? "" : code).replace(/\D/g, "")

    let isInternational = value.charAt(0) === "+"
    let digits = value.replace(/\D/g, "")

    // "00" is the other international prefix address books commonly store.
    if(!isInternational && cc && digits.indexOf(`00${cc}`) === 0) {
        isInternational = true
        digits = digits.substring(2)
    }

    if(isInternational && cc && digits.indexOf(cc) === 0) {
        digits = digits.substring(cc.length)
    }

    // Drop the national trunk prefix. This has to run after the country code
    // has gone, so "+44 (0)7700 900123" is handled and not just "07700...".
    if(digits.charAt(0) === "0") {
        digits = digits.substring(1)
    }

    return digits
}

class ValidationErrors {
    constructor() {
        this.cc = null
        this.city =  null
        this.company_name = null
        this.company_number = null
        this.email = null
        this.person_name = null
        this.postcode = null
        this.sp = null
        this.street = {0: null, 1: null, 2: null}
        this.trading_name = null
        this.voice = null
    }
}

/*
 * The stack-user contact list is a convenience for picking an existing
 * contact; both consumers guard on it being null, so the component works
 * without it. The tag below named /a/reseller/*\/susers, which this file
 * stopped calling when it moved to explicitStackUser -- leaving the new call
 * untagged and quietly adding stackUsersRead to every page using a contact
 * form. The slash is escaped so the URL cannot close this comment.
 *
 * @optional /a/reseller/*\/explicitStackUser
 * Usage
 * -----
 *
 * Add the component to the Vue and make sure the Vue has the following data.
 *
 * contact: {
 *   street: [],
 *   city: "",
 *   sp: "",
 *   cc: "",
 *   email: "",
 *   name: "",
 *   company_name: "",
 *   voice: "",
 *   valid: false,
 * }
 *
 */

/**
 * @typedef contactOptionsIn
 * @property {{extension: {type: string[]}}} options
 * @property {{extension: {type: {[x: string]: string[]}}}} optionAffinity
 * @property {{extension: {type: {[x: string]: string}}}} optionLabels
 * @property {validationOut} validation
 */

 /**
  * @typedef typeOut
  * @property {string} label
  * @property {string} text
  * @property {string} value
  * @property {?{[x: string]: boolean} & {company_name: boolean, company_number: boolean, trading_name: boolean}} fields
  * @property {{[x: string]: string}} labels
  */

/**
 * @typedef validationOut
 * @property {RegExp} email
 * @property {{addr: {city: RegExp, street: RegExp[], pc: RegExp, sp: RegExp}, name: RegExp}} postalInfo
 * @property {RegExp} voice
 * @property {{[x: string]: RegExp}} extension
 */

/**
 * @typedef countryInfo
 * @property {string} text
 * @property {string} value
 * @property {string} name
 * @property {string} code
 * @property {string} class
 * @property {string} voiceCode
 */

/**
 * @typedef contactIn
 * @property {string} address
 * @property {string} cc
 * @property {string} ccAlpha2
 * @property {string} ccAlpha3
 * @property {string} city
 * @property {string} company_name
 * @property {?string} company_number
 * @property {?string} createdAt
 * @property {string} customer
 * @property {string} email
 * @property {string} firstName
 * @property {?string} nominet_contact_type
 * @property {string} pc
 * @property {string} person_name
 * @property {string} sp
 * @property {?string} trading_name
 * @property {?string} vat_number
 * @property {?boolean} verified
 * @property {string} voice
 */

class ContactUI extends NetworkPropertySet {
    constructor() {
        super()
        /** @type {?{result: contactOptionsIn}} */
        this.contactOptions = null
        this.countries = null
        this.countryToTelephoneCode = null
        this.susers = null
    }
    static get inst() {
        if(!this._inst) {
            this._inst = new ContactUI()
        }
        return this._inst
    }
    get allConfirmedFields() {
        return {
            city: true,
            company_name: true,
            company_number: true,
            email: true,
            pc: true,
            person_name: true,
            sp: true,
            street: {0: true, 1: true, 2: true},
            trading_name: true,
            voice: true,
        }
    }
    get contactlist() {
        return this.susers && Object.keys(this.susers.contact).map(ref => {
            let contact = this.susers.contact[ref]
            return {
                id: ref,
                text: `${contact.person_name}, ${contact.address && contact.address.replace(/\n/g, " ")}`
            }
        }).sort((a, b) => a.text.localeCompare(b.text));;
    }
    get networkPropertyHandlers() {
        return {
            contactOptions: () => $.ajax({
                method: "options",
                url: `/a/domain/*/contacts`,
            }),
            countries: () => $.ajax("/assets/etc/all.json").then(results => {
                results.sort((a, b) => a.name.localeCompare(b.name))
                return $.Deferred().resolve(
                    results.filter(
                        c => c["alpha-3"] == "GBR" || c['alpha-3'] == 'USA'
                    ).concat(results)
                )
            }),
            countryToTelephoneCode: () => $.ajax("/app/json/countryToTelephoneCode.json"),
            susers: () => $.ajax(`/a/reseller/*/explicitStackUser`).then( (result) => {
                var out = {
                    contact: {},
                    ftpLock: {},
                    grant_map: {},
                    service_by_tuple: {},
                    users: {}
                };
                for(const su of result){
                    if( su?.contact?.person_name ){
                        out.contact[`stack-user:${su.id}`] = su.contact;
                    }
                }
                return out;
            } ),
        }
    }
    get noConfirmedFields() {
        return {
            city: false,
            company_name: false,
            company_number: false,
            email: false,
            pc: false,
            person_name: false,
            sp: false,
            street: {0: false, 1: false, 2: false},
            trading_name: false,
            voice: false,
        }
    }
}
/**
 * @typedef businessType
 * @property {string} [value]
 * @property {{[x: string]: boolean}} fields
 */
/**
 * @typedef mutableContact The interface for contacts here.
 * @property {string} cc
 * @property {string} city
 * @property {string} email
 * @property {?string} pc
 * @property {string} person_name
 * @property {?string} sp
 * @property {string[]} street
 * @property {boolean} submitted
 * @property {boolean} valid
 * @property {string} voice
 * @property {?string} [company_name]
 * @property {?string} [company_number]
 * @property {?string} [nominet_contact_type]
 * @property {?string} [town]
 * @property {?string} [trading_name]
 */

const type_filters = {
    uk_ind: ["IND"],
    uk_bus: ["CRC", "GOV", "IP", "LLP", "LTD", "OTHER", "PLC", "PTNR", "RCHAR", "SCH", "STAT", "STRA"],
    non_uk_ind: ["FIND"],
    non_uk_bus: ["FOTHER", "FCORP"]
}

class CompanyFromApi {
    /**
     * Company recived from Companies House Api
     * @param {object} cpm
     * @param {boolean} alpha3
     */
    constructor(cpm, alpha3) {
        this.useAlpha3 = alpha3
        this.name = cpm.title
        this.number = cpm.company_number
        this.address_snippet = cpm.address_snippet
        this.address1 = cpm.address.premises
        this.address2 = cpm.address.address_line_1
        this.postcode = cpm.address.postal_code
        this.county = cpm.address.region ? cpm.address.region : null
        this.city = cpm.address.locality
        this.country = this.useAlpha3 ? "GBR" : "GB"
    }
}

const states = [
{
    "State": "Alabama",
    "Abbrev": "Ala.",
    "Code": "AL"
},
{
    "State": "Alaska",
    "Abbrev": "Alaska",
    "Code": "AK"
},
{
    "State": "Arizona",
    "Abbrev": "Ariz.",
    "Code": "AZ"
},
{
    "State": "Arkansas",
    "Abbrev": "Ark.",
    "Code": "AR"
},
{
    "State": "California",
    "Abbrev": "Calif.",
    "Code": "CA"
},
{
    "State": "Colorado",
    "Abbrev": "Colo.",
    "Code": "CO"
},
{
    "State": "Connecticut",
    "Abbrev": "Conn.",
    "Code": "CT"
},
{
    "State": "Delaware",
    "Abbrev": "Del.",
    "Code": "DE"
},
{
    "State": "District of Columbia",
    "Abbrev": "D.C.",
    "Code": "DC"
},
{
    "State": "Florida",
    "Abbrev": "Fla.",
    "Code": "FL"
},
{
    "State": "Georgia",
    "Abbrev": "Ga.",
    "Code": "GA"
},
{
    "State": "Hawaii",
    "Abbrev": "Hawaii",
    "Code": "HI"
},
{
    "State": "Idaho",
    "Abbrev": "Idaho",
    "Code": "ID"
},
{
    "State": "Illinois",
    "Abbrev": "Ill.",
    "Code": "IL"
},
{
    "State": "Indiana",
    "Abbrev": "Ind.",
    "Code": "IN"
},
{
    "State": "Iowa",
    "Abbrev": "Iowa",
    "Code": "IA"
},
{
    "State": "Kansas",
    "Abbrev": "Kans.",
    "Code": "KS"
},
{
    "State": "Kentucky",
    "Abbrev": "Ky.",
    "Code": "KY"
},
{
    "State": "Louisiana",
    "Abbrev": "La.",
    "Code": "LA"
},
{
    "State": "Maine",
    "Abbrev": "Maine",
    "Code": "ME"
},
{
    "State": "Maryland",
    "Abbrev": "Md.",
    "Code": "MD"
},
{
    "State": "Massachusetts",
    "Abbrev": "Mass.",
    "Code": "MA"
},
{
    "State": "Michigan",
    "Abbrev": "Mich.",
    "Code": "MI"
},
{
    "State": "Minnesota",
    "Abbrev": "Minn.",
    "Code": "MN"
},
{
    "State": "Mississippi",
    "Abbrev": "Miss.",
    "Code": "MS"
},
{
    "State": "Missouri",
    "Abbrev": "Mo.",
    "Code": "MO"
},
{
    "State": "Montana",
    "Abbrev": "Mont.",
    "Code": "MT"
},
{
    "State": "Nebraska",
    "Abbrev": "Nebr.",
    "Code": "NE"
},
{
    "State": "Nevada",
    "Abbrev": "Nev.",
    "Code": "NV"
},
{
    "State": "New Hampshire",
    "Abbrev": "N.H.",
    "Code": "NH"
},
{
    "State": "New Jersey",
    "Abbrev": "N.J.",
    "Code": "NJ"
},
{
    "State": "New Mexico",
    "Abbrev": "N.M.",
    "Code": "NM"
},
{
    "State": "New York",
    "Abbrev": "N.Y.",
    "Code": "NY"
},
{
    "State": "North Carolina",
    "Abbrev": "N.C.",
    "Code": "NC"
},
{
    "State": "North Dakota",
    "Abbrev": "N.D.",
    "Code": "ND"
},
{
    "State": "Ohio",
    "Abbrev": "Ohio",
    "Code": "OH"
},
{
    "State": "Oklahoma",
    "Abbrev": "Okla.",
    "Code": "OK"
},
{
    "State": "Oregon",
    "Abbrev": "Ore.",
    "Code": "OR"
},
{
    "State": "Pennsylvania",
    "Abbrev": "Pa.",
    "Code": "PA"
},
{
    "State": "Rhode Island",
    "Abbrev": "R.I.",
    "Code": "RI"
},
{
    "State": "South Carolina",
    "Abbrev": "S.C.",
    "Code": "SC"
},
{
    "State": "South Dakota",
    "Abbrev": "S.D.",
    "Code": "SD"
},
{
    "State": "Tennessee",
    "Abbrev": "Tenn.",
    "Code": "TN"
},
{
    "State": "Texas",
    "Abbrev": "Tex.",
    "Code": "TX"
},
{
    "State": "Utah",
    "Abbrev": "Utah",
    "Code": "UT"
},
{
    "State": "Vermont",
    "Abbrev": "Vt.",
    "Code": "VT"
},
{
    "State": "Virginia",
    "Abbrev": "Va.",
    "Code": "VA"
},
{
    "State": "Washington",
    "Abbrev": "Wash.",
    "Code": "WA"
},
{
    "State": "West Virginia",
    "Abbrev": "W.Va.",
    "Code": "WV"
},
{
    "State": "Wisconsin",
    "Abbrev": "Wis.",
    "Code": "WI"
},
{
    "State": "Wyoming",
    "Abbrev": "Wyo.",
    "Code": "WY"
}
]

// Forcer contactOptions to be loaded.
const UIDATA = ContactUI.inst;
(async function() {
    await UIDATA.preloadSingle("contactOptions");
})();

const ContactStk = Vue.extend({
    template: "#vue-contact",
    props: {
        /**
         * True if it's the main customer relationship contact. Drops "email"
         * from the field list.
         */
        accountcontact: {
            type: Boolean,
            required: false,
            default: false
        },

        /**
         * If true then its a basket edit and the name is editable for all
         * contact types.
         */
        basketEdit: {
            type: Boolean,
            required: false,
            default: false
        },

        /**
         * If true, this contact must have a (Nominet) contact type. This also
         * brings meaning to business_type.
         */
        businesscontact: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * Ignored.
         */
        businesscontactrequired: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * Allow username changes for this contact
         */
        changeusername: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * The main contact data
         */
        contactData: {
            type: [BSContact, Object],
            required: true,
        },
        /**
         * If true, stored contacts should be offered as an alternative.
         */
        contactselector: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * True if person/company name field disablement should be enforced.
         * Mnemonic: you're editing a domain contact for an existing domain. See
         * isNominetDomain
         */
        domaincontactedit: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * True if this contact will be used for a generic (non-Nominet) domain
         * registration. In practice this just means it's invalid to wipe
         * company_name.
         */
        gtldDomain: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * Set to true is the user filling out the contact is doing it for them self
         */
        forSelf: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * True if person/company name field disablement should be enforced.
         * Mnemonic: it's a UK domain. See domaincontactedit
         */
        isNominetDomain: {
            type: Boolean,
            required: false,
            default: false
        },
        lsLayout: {
            type: Boolean,
            required: false,
            default: false,
        },
        /**
         * True if the contact component is being displayed in a solo modal
         * interface
         */
        isModal: {
            type: Boolean,
            required: false,
            default: false,
        },
        /**
         * Force the address form to be showing and bypass the postcode selection
         */
        forceAddressForm: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * Force the address form to be showing and bypass the postcode selection
         */
        forceCompanyForm: {
            type: Boolean,
            required: false,
            default: false
        },
        /**
         * If true, the country code (cc) should be in ISO3166-alpha3 format not
         * ISO3166-alpha2.
         */
        useAlpha3: {
            type: Boolean,
            required: false,
            default: false
        },

        disableFields: {
            type: Boolean,
            required: false,
            default: false,
        },

        /**
         * If true, offer an (optional) VAT number field while the contact's
         * country is in the EU or UK VAT area. The value is not part of
         * contactData: bind it with `v-bind:vat-number.sync` and save it
         * separately (see StackVATNumber), after the contact itself.
         */
        vatNumberField: {
            type: Boolean,
            required: false,
            default: false,
        },

        /**
         * The VAT number, without its country prefix
         */
        vatNumber: {
            type: String,
            required: false,
            default: "",
        },

        /**
         * A problem with the VAT number to show under the field, eg. from
         * the VAT number check
         */
        vatNumberError: {
            type: String,
            required: false,
            default: null,
        },
    },

    data() {
        return {
            businessTypeRequiredByContact: null,
            emailChange: false,
            noAddressFound: false,
            postcodeLookup: {
              postcode: "",
              addresses_returned: false,
              addresses: null,
              selected: ""
            },
            /**
             * @type {?CompanyFromApi[]}
             */
            companyLookup: null,
            companyLookupOtions: null,
            companyLookupNoResults: false,
            selectedCompany: null,
            selected_contact: "",
            selectedPrefixCountry: null,
            /**
             * What the user actually typed/pasted/autofilled into the telephone
             * field, kept verbatim so the field does not rewrite itself under
             * the cursor while a number is being entered. `contactData.voice`
             * is normalised from it on every keystroke, so validation is
             * correct whether or not the field is ever focused; this is only
             * for display, and is cleared on blur so the field then shows the
             * normalised number.
             * @type {?string}
             */
            voiceInputRaw: null,
            states: states,
            ukIndividual: true,
            uiData: UIDATA,
            user: typeof User !== "undefined" ? User.inst : null,
            userConfirmedFields: Object.assign(
                {},
                ContactUI.inst.noConfirmedFields
            ),
            user_type: null,
            find_address: true,
            find_company: true,
            validationErrors: new ValidationErrors(),
        }
    },
    computed: {
        validationErrorValues() {
            for(const key of Object.keys(this.business_type.fields)){
                if( !this.business_type.fields[key] ){
                    this.validationErrors[key] = null;
                }
            }
            return Object.values(
                this.validationErrors
            ).filter( (e) => {
                return e instanceof Object ? Object.values(e).some(m => m) : e;
            }).length
        },

        loading() {
            return this.countries && this.validation ? false : true
        },

        allConfirmed() {
            return this.contactData && this.contactData.submitted
        },

        business_type() {
            let type = this.types && this.types.find(
                type => type.id == this.contactData.nominet_contact_type
            )
            return type || {
              value: "UNKNOWN",
              fields: {
                  company_name: false,
                  trading_name: false,
                  company_number: false,
              }
            }
        },

        businessTypeRequired() {
            return this.businessTypeRequiredByContact || this.businesscontact
        },

        canSelectContacts() {
            return this.contactlist
        },

        contactEntityNumberLabel(){
            switch(this.contactData.nominet_contact_type){
                case 'RCHAR':
                    return translation.get('vue_contact_contact_entity_number_rchar')
                default:
                    return translation.get('vue_contact_contact_entity_number_default')
            }
        },

        contactlist() {
            return this.uiData.contactlist
        },

        contactlistloaded() {
            return !this.contactselector || this.contactlist
        },

        contactsByKey() {
            return this.uiData.susers && this.uiData.susers.contact
        },

        contactTypeLabel() {
            switch(this.contactData.nominet_contact_type){
                case 'RCHAR':
                    return translation.get('vue_contact_contact_type_label_rchar')
                case 'PTNR':
                    return translation.get('vue_contact_contact_type_label_ptnr')
                default:
                    return translation.get('vue_contact_contact_type_label_default')
            }
        },

        company_name_disabled() {
            return(
                !this.basketEdit &&
                this.isNominetDomain &&
                this.domaincontactedit &&
                !(
                    this.contactData.nominet_contact_type == 'IND' ||
                    this.contactData.nominet_contact_type == 'FIND' ||
                    this.contactData.nominet_contact_type == 'STRA' ||
                    this.contactData.nominet_contact_type == ''
                )
            )
        },

        /**
         * @returns {countryInfo[]}
         */
        countries() {
            return(
                this.uiData.countries &&
                this.uiData.countryToTelephoneCode &&
                [{
                    text: translation.get('vue_contact_select_country_label'),
                    id: "",
                    name: "Select Country",
                    code: null,
                    class: null
                }].concat(
                    this.uiData.countries.map(ob => {
                        // UK hack
                        if(ob["alpha-3"] == "GBR") {
                            ob.name = "United Kingdom"
                        }
                        let cc2 =
                            ob["alpha-2"] &&
                            ob["alpha-2"].toLowerCase()
                        return {
                            text: ob.name,
                            id: this.useAlpha3 ?
                                ob["alpha-3"].toUpperCase() :
                                ob["alpha-2"].toUpperCase(),
                            name: ob.name,
                            code: ob["alpha-3"],
                            class: cc2,
                        }
                    })
                ).map(c => {
                    let tel =(
                        c.class &&
                        this.uiData.countryToTelephoneCode.find(
                            t => t.code == c.class.toUpperCase()
                        )
                    )
                    return Object.assign(
                        {
                            voiceCode: tel ?
                                tel.dial_code.replace(" ", "") :
                                ""
                        },
                        c
                    )
                })
            )
        },

        /**
         * @return {?string}
         */
        countryClass() {
            const m = this.countries.find(c => c.code === this.selectedPrefixCountry)
            return m ? m.class : null
        },

        /** @returns {countryInfo} */
        countryInfo() {
            return this.countries.find(
                country => country.code == this.contactData.cc ||
                (
                    country.class &&
                    country.class.toUpperCase() == this.contactData.cc
                )
            )
        },

        /** @returns {countryInfo} */
        voiceInfo() {
            return this.countries.find(
                country => country.code == this.selectedPrefixCountry ||
                (
                    country.class &&
                    country.class.toUpperCase() == this.selectedPrefixCountry
                )
            )
        },

        hasSelectableContacts() {
            return this.canSelectContacts && this.contactselector
        },

        /**
         * @return {boolean}
         */
        isNewContact() {
            return !this.contactData.person_name || this.contactData.person_name === "" ? true : false
        },

        isBussinessEmpty() {
            let name = this.contactData.company_name
            let number = this.contactData.company_number
            let tradingName = this.contactData.trading_name

            return name && name !== "" ||
                number && number !== "" ||
                tradingName && tradingName !== "" ? false : true
        },

        isAddressEmpty() {
            let street1 = this.contactData.street[0]
            let street2 = this.contactData.street[1]
            let street3 = this.contactData.street[2]
            let city = this.contactData.city
            let county = this.contactData.sp

            return street1 && street1 !== "" ||
                street2 && street2 !== "" ||
                street3 && street3 !== "" ||
                city && city !== "" ||
                county && county !== "" ? false : true
        },

        person_name_disabled() {
            return(
                !this.basketEdit &&
                this.isNominetDomain &&
                this.domaincontactedit && (
                    this.contactData.nominet_contact_type == 'IND' ||
                    this.contactData.nominet_contact_type == 'FIND' ||
                    this.contactData.nominet_contact_type == 'STRA' ||
                    this.contactData.nominet_contact_type == ''
                )
            )
        },

        postcodeAddressOptions() {
            if(!this.postcodeLookup.addresses) return null
            return [
                {
                    text: "",
                    id: "",
                }
            ].concat(
                this.postcodeLookup.addresses.map((addr, i) => ({
                    text: addr.label,
                    id: i + 1,
                }))
            )
        },

        stateOptions() {
            return this.states.map(state => ({ text: state.State, id: state.State }))
        },

        /**
         * @returns {?typeOut[]}}
         */
        types() {
            if(!this.uiData.contactOptions) return null

            let default_contact_field_labels = {
                company_name: translation.get('vue_contact_company_name_label'),
                trading_name: translation.get('vue_contact_trading_as_label'),
                company_number: translation.get('vue_contact_company_number_label')
            }
            /** @type {contactOptionsIn} */
            let r = this.uiData.contactOptions.result
            let types = r.options.extension.type.map(type_code => {
                var affinity =
                    r.optionAffinity.extension.type[type_code]
                var label =
                    r.optionLabels.extension.type[type_code]
                return {
                    label: label || type_code,
                    text: label || type_code,
                    id: type_code,
                    fields: affinity ? {
                        company_name: affinity.includes("org"),
                        trading_name: affinity.includes("trad-name"),
                        company_number: affinity.includes("co-no"),
                    } : null,
                    labels: {
                        company_name: default_contact_field_labels.company_name,
                        trading_name: default_contact_field_labels.trading_name,
                        company_number: default_contact_field_labels.company_number
                    }
                }
            })

            if(!this.isNominetDomain && this.domaincontactedit) {
                this.user_type = "individual"
            }

            if(this.user_type === "business") {
                if((this.contactData.cc === "GBR") || (this.contactData.cc === "GB")) {
                    // UK business
                    return types.filter(t => type_filters.uk_bus.includes(t.id))
                }
                // Non UK business
                return types.filter(t => type_filters.non_uk_bus.includes(t.id))
            } else {
                if((this.contactData.cc === "GBR") || (this.contactData.cc === "GB")) {
                    // UK individual
                    return types.filter(t => type_filters.uk_ind.includes(t.id))
                }
                // Non UK individual
                return types.filter(t => type_filters.non_uk_ind.includes(t.id))
            }
        },

        /** @returns {?validationOut} */
        validation() {
            if( this.uiData?.contactOptions?.result?.validation ){
                return this.uiData.contactOptions.result.validation;
            } else {
                return {};
            }
        },

        validationByField() {
            /**
             *
             * @param {?string} v
             * @param {?string} missing_message
             * @param {?RegExp} regexp
             * @param {?string} regexp_message
             * @returns {?string}
             */
            function validate_full(
                v,
                missing_message,
                regexp = null,
                regexp_message = null
            ) {
                if(missing_message && !v) {
                    return missing_message
                } else if(
                    v &&
                    regexp &&
                    !v.match(new RegExp(`^${regexp}$`))
                ) {
                    return regexp_message
                } else {
                    return null
                }
            }
            return {
                cc: v => this.validationErrors.cc = validate_full(
                    v,
                    translation.get('vue_contact_country_is_required'),
                    null,
                    translation.get('vue_contact_please_select_a_country')
                ),
                city: v => this.validationErrors.city = validate_full(
                    v,
                    translation.get('vue_contact_city_is_required'),
                    this.validation.postalInfo?.addr?.city,
                    translation.get('vue_contact_please_enter_a_valid_city')
                ),
                company_name: v => this.validationErrors.company_name = validate_full(
                    v,
                    this.business_type.fields.company_name &&
                        translation.get('vue_contact_company_name_is_required')
                ),
                company_number: v => {
                    if(this.businessTypeRequired && this.business_type.fields.company_number) {
                        if(this.user_type === 'business') {
                            return this.validationErrors.company_number = validate_full(
                                v,
                                this.business_type.fields.company_number &&
                                    translation.get('vue_contact_company_number_is_requierd'),
                                this.validation.extension["co-no"],
                                translation.get('vue_contact_please_enter_valid_company_number'))
                        } else {
                            return this.validationErrors.company_number = null
                        }
                    }
                    return this.validationErrors.company_number = null
                },
                email: v => this.validationErrors.email = validate_full(
                    v.toLocaleLowerCase().trim(),
                    this.accountcontact ? null : translation.get('vue_contact_email_is_required'),
                    this.validation.email,
                    translation.get('vue_contact_email_address_looks_invalid')
                ),
                pc: v => this.validationErrors.postcode = validate_full(
                    v,
                    this.contactData.cc && this.contactData.cc.match(
                        /^(CAN?|GBR?|USA?)$/
                    ) ?
                        translation.get('vue_contact_postcode_is_required') :
                        null,
                    this.validation.postalInfo?.addr?.pc,
                    translation.get('vue_contact_please_enter_valid_postcode')
                ),
                person_name: v => this.validationErrors.person_name = validate_full(
                    v,
                    translation.get('vue_contact_name_is_required'),
                    this.validation.postalInfo?.name,
                    translation.get('vue_contact_please_enter_valid_name')
                ),
                sp: v => this.validationErrors.sp = validate_full(
                    v,
                    this.contactData.cc == "USA" ?
                        "State is required" :
                        "County/State/Province is required", // "County is required",
                    this.validation.postalInfo?.addr?.sp,
                    this.contactData.cc == "USA" ?
                        translation.get('vue_contact_please_enter_valid_state') :
                        translation.get('vue_contact_please_enter_valid_county_state_province')
                ),
                street: [
                    v => this.validationErrors.street[0] = validate_full(
                        v,
                        translation.get('vue_contact_address_is_required'),
                        this.validation.postalInfo?.addr?.street[0],
                        translation.get('vue_contact_please_enter_valid_address')
                    ),
                    v => this.validationErrors.street[1] = validate_full(
                        v,
                        null,
                        this.validation.postalInfo?.addr?.street[1],
                        translation.get('vue_contact_please_enter_valid_address')
                    ),
                    v => this.validationErrors.street[2] = validate_full(
                        v,
                        null,
                        this.validation.postalInfo?.addr?.street[2],
                        translation.get('vue_contact_please_enter_valid_address')
                    ),
                ],
                trading_name: v => this.validationErrors.trading_name = validate_full(
                    v,
                    null,
                    this.validation.extension ? this.validation.extension["trad-name"] : null,
                    translation.get('vue_contact_please_enter_valid_trading_name')
                ),
                voice: v => this.validationErrors.voice = validate_full(
                    v,
                    translation.get('vue_contact_telephone_is_required'),
                    this.validation.voice,
                    translation.get('vue_contact_please_use_international_format')
                ),
            }
        },

        /**
         * @return {?string}
         */
        voiceCode() {
            if(this.voiceInfo) {
                let code = this.voiceInfo.voiceCode
                // Re-assemble against the newly picked prefix. Normalise as we
                // go, so switching country cannot leave the previous country's
                // code stranded in the national part.
                let digits = normaliseNationalNumber(this.voice_temp, code)
                if(digits) {
                    this.contactData.voice = `${code}.${digits}`
                }
                return code + '.'
            } else {
                return null
            }
        },

        voice_temp: {
            get() {
                // While the field is being edited, show it back exactly as
                // entered; otherwise derive the national part from the stored
                // canonical "+CC.NUMBER".
                if(this.voiceInputRaw !== null) {
                    return this.voiceInputRaw
                }
                return(
                    this.contactData.voice &&
                    this.contactData.voice.split(/[.]/)[1]
                )
            },
            set(v) {
                if(this.countryInfo) {
                    this.voiceInputRaw = v == null ? "" : v
                    let digits = normaliseNationalNumber(v, this.countryInfo.voiceCode)
                    this.contactData.voice = digits
                        ? this.countryInfo.voiceCode + '.' + digits
                        : ""
                }
            }
        },

        has_selected_postcode() {
            return this.postcodeLookup.selected !== "" && this.postcodeLookup.selected !== "0" ? true : false
        },

        /**
         * A VAT number only changes the tax for customers in the EU / UK VAT
         * areas, so the field is only offered there.
         *
         * @returns {boolean}
         */
        vatNumberVisible() {
            return !!(
                this.vatNumberField &&
                typeof TaxDisplay != "undefined" &&
                TaxDisplay.isVatArea(this.contactData.cc)
            )
        },

        /**
         * @returns {?string} eg. "DE", or "EL" for Greece
         */
        vatNumberPrefix() {
            return this.vatNumberVisible ?
                TaxDisplay.vatPrefix(this.contactData.cc) :
                null
        },

        vatNumberModel: {
            get() {
                return this.vatNumber || ""
            },
            set(v) {
                this.$emit("update:vatNumber", v)
            }
        },

        /**
         * @returns {{label: string, optional: string, hint: string}}
         */
        vatNumberText() {
            const t = (k, f) => TaxDisplay.translate(k, f)
            return {
                label: t("vue_contact_vat_number_label", "VAT number"),
                optional: t("vue_contact_optional", "(optional)"),
                hint: t(
                    "vue_contact_vat_number_hint",
                    "For VAT-registered businesses outside our country: VAT isn't charged and you account for it yourself (reverse charge)."
                ),
            }
        },
    },

    watch: {
        "validationErrorValues": function(errors) {
            this.contactData.valid = !errors
            this.$emit("update:errors", errors)
        },
        "contactData.cc": function(v) {
            this.selectedPrefixCountry = v
            this.normalisePostcode()
            if(v === "GBR" || v === "GB") {
                this.ukIndividual = true

                this.isBussinessEmpty ? this.find_company = true : this.find_company = false

                this.isAddressEmpty ? this.find_address = true : this.find_address = false
            } else {
                this.ukIndividual = false
                this.find_company = false
                this.find_address = false
                this.resetCompanyLookup()
            }
            // Expand company based on existing values
        },
        "contactData.email": function(n,o) {
            if(n != o){
                this.emailChange = true;
            }
        },
        "contactData.nominet_contact_type": function(v) {
            Object.keys(this.validationByField).forEach(
                field => {
                    if(this.validationByField[field] instanceof Function) {
                        this.validationByField[field](
                            this.contactData[field]
                        )
                    } else {
                        Object.keys(this.validationByField[field]).forEach(
                            subfield => this.validationByField[field][subfield](
                                this.contactData[field][subfield]
                            )
                        )
                    }
                }
            )
        },
        "contactData.pc": function(v) {
            this.normalisePostcode()
        },
        "contactData.submitted": function(v) {
            if(v) {
                this.userConfirmedFields = Object.assign(
                    {},
                    ContactUI.inst.allConfirmedFields
                )
            } else {
                this.userConfirmedFields = Object.assign(
                    {},
                    ContactUI.inst.noConfirmedFields
                )
            }
        },
        "postcodeLookup.selected": function(v) {
            if(v != 0) {
                let new_address = this.postcodeLookup.addresses[v - 1]

                this.contactData.street = [
                    new_address.address_1,
                    new_address.address_2 || "",
                    ""
                ]
                this.contactData.town = new_address.town
                this.contactData.city = new_address.town
                this.contactData.sp = new_address.county || ""
                this.postcodeLookup.addresses = null
            }
            // this.$emit("update:contactData", this.contact)
        },
        selected_contact(v) {
            let selected_contact = this.contactsByKey[v]
            console.log(selected_contact)
            if(selected_contact) {
                this.$emit("update:contactData", Object.assign(
                    this.contactData instanceof BSContact ? new BSContact(null) : {},
                    this.contactData,
                    {
                        street: selected_contact.address ?
                            (selected_contact.address + "\n\n").split("\n", 3) :
                            ["", "", ""],
                        city: selected_contact.city,
                        sp: selected_contact.sp,
                        email: selected_contact.email,
                        voice: selected_contact.voice,
                        person_name: selected_contact.person_name,
                        pc: selected_contact.pc,
                        company_name: selected_contact.company_name,
                        company_number: selected_contact.company_number,
                        trading_name: selected_contact.trading_name,
                        cc: this.useAlpha3 ?
                            selected_contact.ccAlpha3 :
                            selected_contact.ccAlpha2,
                        nominet_contact_type: selected_contact.nominet_contact_type,
                        submitted: true,
                    }
                ))
                if(
                    selected_contact.company_name ||
                    selected_contact.company_number ||
                    selected_contact.trading_name
                ){
                    this.businessTypeRequiredByContact = true
                }

                Vue.set(this, 'contactData', this.contactData)

                if(this.userHaveBusiness(this.contactData)) {
                    this.user_type = "business"
                } else {
                    this.user_type = "individual"
                }

            } else {
                this.$emit("update:contactData", Object.assign(
                    {},
                    this.contactData,
                    {
                        street: ["", "", ""],
                        city: "",
                        sp: "",
                        email: "",
                        voice: "",
                        person_name: "",
                        pc: "",
                        company_name: "",
                        company_number: "",
                        trading_name: "",
                        cc: this.useAlpha3 ? "GBR" : "GB",
                        nominet_contact_type: "",
                        submitted: false,
                    }
                ))
            }
        },
        validation(v) {
            if(v) {
                Object.keys(this.validationByField).forEach(
                    field => {
                        if(this.validationByField[field] instanceof Function) {
                            this.validationByField[field](
                                this.contactData[field]
                            )
                        } else {
                            Object.keys(this.validationByField[field]).forEach(
                                subfield => this.validationByField[field][subfield](
                                    this.contactData[field][subfield]
                                )
                            )
                        }
                    }
                )
            }
        },

        types(v) {
            this.$nextTick(() => {
                if(!this.contactData.nominet_contact_type) {
                    this.contactData.nominet_contact_type = v[0].id
                }
            })
        },

        loading(v) {
            this.$nextTick(() => {
                $('span.details-tooltip[data-bs-toggle="tooltip"]').tooltip()
            })
        },

        selectedCompany(v) {
            if(this.companyLookup) {
                let m = this.companyLookup.find(el => el.name === v)
                if(m) {
                    this.assignCompany(m, () => {
                        this.resetCompanyLookup()
                    })
                }
            }
        },
        "contactData.street": function(n, o) {
            var haveChange = false
            var newArr = []
            for(const l of n) {
                var nl = null
                if(l) {
                    var nl = l.replace(/,$/,"")
                    if(nl != l) {
                        haveChange = true
                    }
                }
                newArr.push(nl)
            }
            if(haveChange) {
                this.$nextTick(() => {
                    this.contactData.street = newArr
                })
            }
        },

        user_type(v) {
            if(this.types) {
                // Keep an existing type (eg. PTNR) if it's valid for the new user type
                const current = this.contactData.nominet_contact_type
                if(current && this.types.some(t => t.id == current)) {
                    return
                }
                if( this.contactData.eppContact && this.contactData.eppContact.extension && this.contactData.eppContact.extension.type ){
                    switch(this.contactData.eppContact.extension.type){
                        case 'GOV':
                            this.contactData.nominet_contact_type = "GOV";
                            break;
                        default:
                            this.contactData.nominet_contact_type = this.types[0].id
                    }
                } else {
                    this.contactData.nominet_contact_type = this.types[0].id
                }
            }
        }
    },
    methods: {
        /**
         * Telephone field blur. Confirms the field so its validation message
         * can show, and drops the verbatim copy of what was entered so the
         * field falls back to displaying the normalised national number.
         */
        confirmVoice() {
            this.userConfirmedFields.voice = true
            this.voiceInputRaw = null
        },

        localStorageBusiness() {
            this.$nextTick(() => {
                if(this.user_type === 'individual') {
                    if(this.contactData.company_number && this.contactData.company_number !== "") {
                        localStorage.setItem('contact_business_company_number', this.contactData.company_number)
                    }

                    if(this.contactData.trading_name && this.contactData.trading_name !== "") {
                        localStorage.setItem('contact_business_trading_name', this.contactData.trading_name)
                    }

                    if(this.contactData.company_name && this.contactData.company_name !== "") {
                        localStorage.setItem('contact_business_company_name', this.contactData.company_name)
                    }

                    this.contactData.company_number = ""
                    this.contactData.trading_name = ""
                    this.contactData.company_name = ""
                } else {
                    if(localStorage.hasOwnProperty('contact_business_company_number')) {
                        this.contactData.company_number = localStorage.getItem('contact_business_company_number')
                    }

                    if(localStorage.hasOwnProperty('contact_business_trading_name')) {
                        this.contactData.trading_name = localStorage.getItem('contact_business_trading_name')
                    }

                    if(localStorage.hasOwnProperty('contact_business_company_name')) {
                        this.contactData.company_name = localStorage.getItem('contact_business_company_name')
                    }
                }
            })
        },

        setInitialPrefix() {
            this.selectedPrefixCountry = this.contactData.cc
        },

        clearBusinnessLocalstorage() {
            localStorage.removeItem('contact_business_company_number')
            localStorage.removeItem('contact_business_trading_name')
            localStorage.removeItem('contact_business_company_name')
        },

        lookup_postcode() {
            var postcode = this.contactData.pc || ""
            this.postcodeLookup.addresses = null

            this.postcodeLookup.selected = "0"

            function canonicalise(label) {
                return label.replace(
                    /\d+/,
                    md => "00000".substring(0, 5 - md.length) + md
                ).split(/, /).reverse().join(", ")
            }

            $.ajax(`/a/address/${encodeURIComponent(postcode)}`).done(addrs => {
                if(addrs) {
                    addrs = addrs.sort((a, b) => {
                        var al = canonicalise(a.label)
                        var bl = canonicalise(b.label)
                        return al < bl ? -1 : bl < al ? 1 : 0
                    })
                    this.postcodeLookup.addresses = addrs
                    this.$nextTick(() => {
                        this.contactData.cc = this.useAlpha3 ? "GBR" : "GB"
                        this.noAddressFound = false
                    })
                } else {
                    this.noAddressFound = true
                }
            }).fail(() => ShowNotification.fail(
                translation.get('vue_contact_problem_looking_up_addresses')
            ))
        },
        normalisePostcode() {
            let match
            if(
                this.contactData.pc &&
                (this.contactData.cc == "GBR" || this.contactData.cc == "GB") &&
                !this.contactData.pc.match(/ /) &&
                (match = this.contactData.pc.match(/^(.*)(\d([a-z]|[A-Z]){2,})$/))
            ) {
                this.contactData.pc =
                    (match[1] + " " + match[2]).toUpperCase()
            }
            if(this.contactData.pc) {
                this.validationByField.pc(this.contactData.pc)
            }
        },

        searchCompanies() {
            let q = this.contactData.company_name
            if(q && q !== '') {
                this.postcodeLookup.addresses = null
                $.ajax(`/a/search-companies/${q}`)
                    .done(r => {
                        this.companyLookup = []
                        this.companyLookupOtions = []

                        if(r.length) {
                            this.companyLookupNoResults = false
                            r.forEach(el => {
                                this.companyLookup.push(new CompanyFromApi(el, this.useAlpha3))
                            })
                            this.assembleCompaniesOptions()
                        } else {
                            this.companyLookupNoResults = true
                            this.find_company = false
                        }
                    })
            }
        },

        assembleCompaniesOptions() {
            this.companyLookupOtions = []
            this.companyLookup.forEach(cpm => {
                this.companyLookupOtions.push({
                    id: cpm.name,
                    text: `${cpm.name} - ${cpm.address_snippet}`
                })
            })
        },

        /**
         * @param {CompanyFromApi} company
         * @param {?Function} callback
         */
        assignCompany(company, callback = null) {
            let addressParts = [company.address1,company.address2];
            addressParts = addressParts.filter(p => !!p);

            this.contactData.trading_name = company.name
            this.contactData.pc = company.postcode
            this.contactData.cc = company.country
            this.contactData.city = company.city
            this.contactData.company_number = company.number
            this.contactData.sp = company.county
            if( addressParts[0] ){
                this.contactData.street[0] = addressParts[0];
            }
            if( addressParts[1] ){
                this.contactData.street[1] = addressParts[1];
            }
            this.find_address = false
            this.find_company = false

            if(callback) {
                callback()
            }
        },

        setUserType() {
            if(this.isNewContact) {
                this.businesscontact ? this.user_type = "business" : this.user_type = "individual"
            } else {
                if(
                    this.contactData.nominet_contact_type === "IND" ||
                    this.contactData.nominet_contact_type === "FIND"
                ) {
                    this.user_type = "individual";
                } else {
                    this.user_type = "business";
                }
                this.find_address = false
                this.find_company = false
            }
        },

        resetCompanyLookup() {
            this.selectedCompany = null
            this.companyLookup = null
            this.companyLookupOtions = null
        },

        /**
         * @param {{id: string, text: string}} option
         */
        formatTelephoneResults: function(option) {
            let cls = this.countries.filter(el => el.code === option.id)

            if(!cls.length) {
                cls = this.countries.filter(el => el.value === option.id)
            }

            if(cls.length) {
                let flagClass = cls[0].class
                return $(`<span><span class="me-2 flag-icon flag-icon-${flagClass}"></span> ${option.text}</span>`)
            }

            return option.text
        },

        /**
         * @param {{id: string, text: string}} option
         */
        formatTelephoneSelection: function(option) {
            let cls = this.countries.filter(el => el.code === option.id)

            if(!cls.length) {
                cls = this.countries.filter(el => el.value === option.id)
            }

            if(cls.length) {
                let flagClass = cls[0].class
                return $(`<span class="flag-icon flag-icon-${flagClass}" style="margin-right: 0.3125rem;"></span>`)
            }

            return option.text
        },

        /**
         * @param {BSContact} user
         * @return {boolean}
         */
        userHaveBusiness(user) {
            if(
                (user.company_name && user.company_name !== '') ||
                (user.company_number && user.company_number !== '') ||
                (user.trading_name && user.trading_name !== '')
            ) {
                return true
            }

            return false
        }
    },
    components: {
        vueSelect: VueSelect,
        textField: TextField,
    },
    async mounted() {
        if (this.forceCompanyForm) {
            this.find_company = false
        }
        // Hook up all the validation watchers
        Object.keys(this.validationByField).forEach(
            field => {
                if(this.validationByField[field] instanceof Function) {
                    this.$watch(
                        () => this.contactData[field],
                        this.validationByField[field]
                    )
                } else {
                    Object.keys(this.validationByField[field]).forEach(
                        subfield => this.$watch(
                            () => this.contactData[field][subfield],
                            this.validationByField[field][subfield]
                        )
                    )
                }
            }
        )
        // Tidy numbers that arrive from somewhere other than the field itself
        // -- an existing contact picked from the list, a lookup result -- since
        // those can carry a trunk prefix stored before entry was normalised.
        // Typing and autofill are handled by the voice_temp setter instead.
        //
        // This replaces a watcher that tried to correct the number on every
        // keystroke. It only ran when the assembled value failed the loose EPP
        // regex, and "+44.4407700900123" passes that, so an autofilled number
        // reached the payment stage invalid; and because it rewrote the field
        // mid-type, a leading "0" could never be entered by hand.
        this.$watch(
            () => this.contactData.voice,
            function(v) {
                // Skip while the user is in the field: the setter has already
                // normalised this value, and rewriting it here would fight the
                // raw text being displayed back to them.
                if(!v || this.voiceInputRaw !== null || !this.voiceCode) {
                    return
                }
                let code = this.voiceCode.split(".")[0]
                let digits = normaliseNationalNumber(v.split(/[.]/)[1], code)
                if(digits) {
                    this.contactData.voice = `${code}.${digits}`
                }
            }
        )

        this.clearBusinnessLocalstorage()
        this.setUserType()
        this.setInitialPrefix()
    }
})

Vue.component('contact-stk', ContactStk)
